import { ref } from 'vue'

/**
 * Light or dark, chosen by the user from the moon / sun next to the language
 * switch. Light is the default: the public pages also open on customers' locked
 * iPhones, which never pick a theme and should look as designed. The choice is
 * remembered per browser, like the interface language.
 *
 * The theme is the `data-theme` attribute of <html>: tokens.css switches the
 * palette on it, and PrimeVue its dark colour scheme (src/main.ts). index.html
 * sets it before the app loads, so a dark console never flashes white.
 */
export type ThemeMode = 'light' | 'dark'

const STORAGE_KEY = 'mdm.theme'

/** The top bar colour of a phone browser, per theme: the page colour. */
const THEME_COLOR: Record<ThemeMode, string> = { light: '#ffffff', dark: '#1c1c1c' }

function initialMode(): ThemeMode {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

export const themeMode = ref<ThemeMode>(initialMode())

function apply(mode: ThemeMode): void {
  document.documentElement.dataset.theme = mode
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[mode])
}

apply(themeMode.value)

export function setThemeMode(mode: ThemeMode): void {
  themeMode.value = mode
  apply(mode)
  try {
    localStorage.setItem(STORAGE_KEY, mode)
  } catch {
    // Private mode: only this visit keeps it.
  }
}

export function toggleThemeMode(): void {
  setThemeMode(themeMode.value === 'dark' ? 'light' : 'dark')
}
